import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { GetKycResponse } from "@/api/queries";

// Helper to build the SA-API URL for a given clone environment
export function getCloneApiUrl(cloneEnvironment: number | "default" | null): string {
  const defaultUrl = import.meta.env.VITE_API as string;

  if (cloneEnvironment === null || cloneEnvironment === "default") {
    return defaultUrl;
  }

  if (typeof cloneEnvironment === "number" && cloneEnvironment >= 1 && cloneEnvironment <= 10) {
    return `https://clone-${cloneEnvironment}-sa-api.publishingdashboard.com/api/v1`;
  }

  return defaultUrl;
}

const to = new Date();
// Set a more reasonable default date range - last 12 months instead of from 2000
const from = new Date(to);
from.setFullYear(from.getFullYear() - 1);

// Your existing types (keep them as they are)
export type AuthenticationType = Record<string, unknown>;
export type KYCType = GetKycResponse | null;
export type HubspotType = Record<string, unknown>;

export type OnboardingType = {
  howHear: string | null;
  role: string | null;
  accountName: string | null;
  address1: string | null;
  address2: string | null;
  town: string | null;
  county: string | null;
  postcode: string | null;
  country: string | null;
};

export type AddAccountType = {
  name: string | null;
  cataloguecode?: string | null;
  live: boolean | null;
  streaming: boolean | null;
  spotify: string | null;
  facebook: string | null;
  twitter: string | null;
};

export type SongsTableType = {
  page: number;
  perPage: number;
  sortBy: Array<{ id: string; desc: boolean }> | null; // Changed to match original
  globalFilter: string | null;
};

export type DateRangeFormat = {
  format: string;
  gte: string;
  lte: string;
};

export type RangeType = {
  receiptDate: DateRangeFormat;
  publishedStatementDate: DateRangeFormat;
};

export type SelectedDatesType = {
  fromDate: string;
  toDate: string;
};

export type DataRangeType = {
  from: Date | null;
  to: Date | null;
};

export type SiteConfigType = {
  display_name?: string;
  spotify_api_url?: string;
  [key: string]: unknown;
};

// Simplified state type - just the data, actions separate
type AppStateData = {
  authentication: AuthenticationType;
  kyc: KYCType;
  hubspot: HubspotType;
  selectedAccount: unknown;
  allSyncsAvailable: boolean | null;
  allOpportunitiesAvailable: unknown[] | null;
  selectedCatalogue: unknown;
  tenantCurrencyCode: string;
  tenantCurrencySymbol: string;
  tenantCurrencyLocale: string;
  serverError: Error | null;
  onboarding: OnboardingType;
  addAccount: AddAccountType;
  spotifyAccountMethod: string;
  agreedTerms: boolean;
  songsTable: SongsTableType;
  range: RangeType;
  royaltiesFilter: unknown[];
  navItemsEnabled: boolean;
  apiUrl: string;
  hubspotDisabled: boolean | null;
  clientDisplayName: string;
  siteConfig?: SiteConfigType;
  isAccountSwitching: boolean;
  cloneEnvironment: number | "default" | null;
  previewStatements: boolean;
};

// Actions type
type AppStateActions = {
  updateState: (updates: Partial<AppStateData>) => void; // Generic update action from JS version
  updateAddAccount: (updates: Partial<AddAccountType>) => void;
  updateOnboarding: (updates: Partial<OnboardingType>) => void;
  updateSongsTable: (updates: Partial<SongsTableType>) => void;
  setSelectedAccount: (account: unknown) => void;
  setSelectedCatalogue: (catalogue: unknown) => void;
  setServerError: (error: Error | null) => void;
  setRoyaltiesFilter: (filters: unknown[]) => void;
  setAccountSwitching: (switching: boolean) => void;
  setCloneEnvironment: (env: number | "default" | null) => void;
};

// Combined type
export type AppState = AppStateData & AppStateActions;

// Initial state matching your original exactly
const initialState: AppStateData = {
  authentication: {},
  kyc: null,
  hubspot: {},
  selectedAccount: null,
  allSyncsAvailable: null,
  allOpportunitiesAvailable: null,
  selectedCatalogue: null,
  tenantCurrencyCode: "",
  tenantCurrencySymbol: "*",
  tenantCurrencyLocale: "",
  serverError: null,
  onboarding: {
    howHear: null,
    role: null,
    accountName: null,
    address1: null,
    address2: null,
    town: null,
    county: null,
    postcode: null,
    country: null,
  },
  addAccount: {
    name: null,
    cataloguecode: null,
    live: null,
    streaming: null,
    spotify: null,
    facebook: null,
    twitter: null,
  },
  spotifyAccountMethod: "",
  agreedTerms: false,
  songsTable: {
    page: 1,
    perPage: 10,
    sortBy: [{ id: "createddate", desc: true }],
    globalFilter: null,
  },
  range: {
    receiptDate: {
      format: "strict_date_optional_time",
      gte: from.toISOString(),
      lte: to.toISOString(),
    },
    publishedStatementDate: {
      format: "strict_date_optional_time",
      gte: from.toISOString(),
      lte: to.toISOString(),
    },
  },
  royaltiesFilter: [],
  navItemsEnabled: true,
  apiUrl: import.meta.env.VITE_API as string,
  hubspotDisabled: null,
  clientDisplayName: "",
  isAccountSwitching: false,
  cloneEnvironment: null,
  previewStatements: false,
};

const useAppStore = create<AppState>()(
  persist(
    set => ({
      ...initialState,

      // Generic action from JS version - just like AppState.update()
      updateState: updates => set(state => ({ ...state, ...updates })),

      // Specific actions for common operations
      updateAddAccount: updates => set(state => ({
        addAccount: { ...state.addAccount, ...updates },
      })),

      updateOnboarding: updates => set(state => ({
        onboarding: { ...state.onboarding, ...updates },
      })),

      updateSongsTable: updates => set(state => ({
        songsTable: { ...state.songsTable, ...updates },
      })),

      setSelectedAccount: (account) => {
        // Also persist to localStorage for cross-session recovery
        if (account != null && typeof account === "string" && account.length > 0) {
          try {
            localStorage.setItem("@lastSelectedAccount", account);
          }
          catch (e) {
            console.warn("[AppStore] Failed to persist account to localStorage:", e);
          }
        }
        set({ selectedAccount: account });
      },
      setSelectedCatalogue: catalogue => set({ selectedCatalogue: catalogue }),
      setServerError: error => set({ serverError: error }),
      setRoyaltiesFilter: filters => set({ royaltiesFilter: filters }),
      setAccountSwitching: switching => set({ isAccountSwitching: switching }),
      setCloneEnvironment: env => set({ cloneEnvironment: env }),
    }),
    {
      name: "@userData", // Use same key as original
      storage: createJSONStorage(() => sessionStorage), // Kept as sessionStorage for security

      // Enhanced merge function with full migration logic from JS version
      merge: (persistedState, currentState) => {
        if (typeof persistedState !== "object" || persistedState === null) {
          return currentState;
        }

        const state = { ...currentState, ...persistedState } as AppState;

        // Apply same migrations as original JS version
        // eslint-disable-next-line ts/strict-boolean-expressions
        if (state.songsTable) {
          state.songsTable = {
            page: 1,
            perPage: 10,
            sortBy: Array.isArray(state.songsTable.sortBy) ? state.songsTable.sortBy : [],
            globalFilter: state.songsTable.globalFilter ?? null,
          };

          // eslint-disable-next-line ts/strict-boolean-expressions
          if (!state.songsTable.globalFilter) {
            state.songsTable.globalFilter = null;
          }

          if (!Array.isArray(state.songsTable.sortBy)) {
            state.songsTable.sortBy = [];
          }
        }

        // eslint-disable-next-line ts/strict-boolean-expressions
        if (!state.range) {
          const migrationTo = new Date();
          const migrationFrom = new Date(migrationTo);
          migrationFrom.setFullYear(migrationFrom.getFullYear() - 1);

          state.range = {
            receiptDate: {
              format: "strict_date_optional_time",
              gte: migrationFrom.toISOString(),
              lte: migrationTo.toISOString(),
            },
            publishedStatementDate: {
              format: "strict_date_optional_time",
              gte: migrationFrom.toISOString(),
              lte: migrationTo.toISOString(),
            },
          };
        }

        // Always update to use reasonable defaults instead of 2000-01-01
        const migrationTo = new Date();
        const migrationFrom = new Date(migrationTo);
        migrationFrom.setFullYear(migrationFrom.getFullYear() - 1);

        state.range = {
          receiptDate: {
            format: "strict_date_optional_time",
            gte: migrationFrom.toISOString(),
            lte: migrationTo.toISOString(),
          },
          publishedStatementDate: {
            format: "strict_date_optional_time",
            gte: migrationFrom.toISOString(),
            lte: migrationTo.toISOString(),
          },
        };

        if (!Array.isArray(state.royaltiesFilter)) {
          state.royaltiesFilter = [];
        }

        if (state.navItemsEnabled === undefined) {
          state.navItemsEnabled = true;
        }

        // This ensures clone environment survives page refresh
        if (state.cloneEnvironment !== null && state.cloneEnvironment !== "default" && typeof state.cloneEnvironment === "number") {
          state.apiUrl = getCloneApiUrl(state.cloneEnvironment);
        }

        return state;
      },
    },
  ),
);

// Helper to persist account to localStorage for cross-session recovery
export function persistAccountToLocalStorage(accountCode: string | null): void {
  try {
    if (accountCode != null && typeof accountCode === "string" && accountCode.length > 0) {
      localStorage.setItem("@lastSelectedAccount", accountCode);
    }
  }
  catch (e) {
    console.warn("[AppStore] Failed to persist account to localStorage:", e);
  }
}

// Helper to get the last selected account from localStorage
export function getLastSelectedAccount(): string | null {
  try {
    return localStorage.getItem("@lastSelectedAccount");
  }
  catch (e) {
    console.warn("[AppStore] Failed to read last account from localStorage:", e);
    return null;
  }
}

// Helper to clear last selected account (e.g., on logout)
export function clearLastSelectedAccount(): void {
  try {
    localStorage.removeItem("@lastSelectedAccount");
  }
  catch (e) {
    console.warn("[AppStore] Failed to clear last account from localStorage:", e);
  }
}

// Helper to persist default account to localStorage
export function setDefaultAccount(accountCode: string | null): void {
  try {
    if (accountCode != null && typeof accountCode === "string" && accountCode.length > 0) {
      localStorage.setItem("@defaultAccount", accountCode);
    }
    else {
      localStorage.removeItem("@defaultAccount");
    }
  }
  catch (e) {
    console.warn("[AppStore] Failed to set default account in localStorage:", e);
  }
}

// Helper to get the default account from localStorage
export function getDefaultAccount(): string | null {
  try {
    return localStorage.getItem("@defaultAccount");
  }
  catch (e) {
    console.warn("[AppStore] Failed to read default account from localStorage:", e);
    return null;
  }
}

// Helper to clear default account
export function clearDefaultAccount(): void {
  try {
    localStorage.removeItem("@defaultAccount");
  }
  catch (e) {
    console.warn("[AppStore] Failed to clear default account from localStorage:", e);
  }
}

export default useAppStore;
